import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate, useParams, useLocation } from 'react-router-dom';
import { IcUndo, IcSearch, IcPlus, IcClose, IcSave, IcUser, IcReceiptText, IcPackageOpen, IcPieChart, IcTrash, IcScanBarcode } from '../../theme/icons';
import Swal from '../../theme/swal';
import KI from '../../theme/palette';
import authService from '../../services/authService';
import { requestIdentity, withIdentity } from '../../services/identityGate';
import supplierService from '../../services/supplierService';
import productService from '../../services/productService';
import factureService from '../../services/factureService';
import commandeService from '../../services/commandeService';
import categoryService from '../../services/categoryService';
import subcategoryService from '../../services/subcategoryService';
import laboratoireService from '../../services/laboratoireService';
import {
  normProductName, duplicateProductAlert, extractApiErrorMessage, isDuplicateProductNameError,
} from '../../utils/duplicateProduct';
import { useAppearance } from '../../context/AppearanceContext';
import { useCompany } from '../../context/CompanyContext';
import { CardSkeleton } from '../../components/Skeleton';
import { filterSearch } from '../../utils/search';

// The timbre fiscal is itself a tax, so it is NEVER subject to TVA. Any line
// whose product is a timbre stays at 0% — on selection, on edit, and when an
// existing facture is reloaded. The backend enforces the same rule
// (factures/api_views.py), so the total shown here always matches the stored one.
const isTimbre = (name) => /timbre/i.test(name || '');

// La monnaie est en millimes : toujours 3 décimales exactement. Les flottants JS
// dérivent (53.193 × 1.19 = 63.299670000000006), donc chaque agrégat monétaire
// est arrondi au millime — sinon le total affiché (64.300) et le total interne
// (64.29966999999999) divergent et le montant payé saisi est rejeté/écrasé.
const round3 = (n) => Math.round(((Number(n) || 0) + Number.EPSILON) * 1000) / 1000;

// Mêmes recettes que /products/add et /clients/add (owner 2026-09-09) : champ
// `.inp` 13.5px py-2.5, libellé 12px 500 encre mb-1.5, aide 11px gris,
// suffixe DT gris posé à droite du champ.
const inputClass = 'inp w-full px-3 py-2.5 text-sm text-gray-900';
const labelClass = 'block text-xs font-medium text-gray-900 mb-1.5';
const helpClass = 'text-[11px] text-gray-400 mt-1.5';
const suffixClass = 'absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400 pointer-events-none';

const AddFacturation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { text: appText, flag: appFlag } = useAppearance();
  const { id } = useParams(); // Get facture ID from route params for edit mode
  const isEditMode = Boolean(id);
  // Réception d'un bon de commande : la page arrive pré-remplie
  // (fournisseur + lignes) et, une fois la facture créée, le BC passe à RECU.
  const commandePrefill = !isEditMode ? location.state?.commande : null;
  const [loading, setLoading] = useState(false);
  const [loadingFacture, setLoadingFacture] = useState(isEditMode);

  // Supplier section
  const [suppliers, setSuppliers] = useState([]);
  const [filteredSuppliers, setFilteredSuppliers] = useState([]);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [supplierSearch, setSupplierSearch] = useState('');
  const [showSupplierDropdown, setShowSupplierDropdown] = useState(false);

  // Add new supplier form
  const [showAddSupplier, setShowAddSupplier] = useState(false);
  const [newSupplier, setNewSupplier] = useState({
    name: '',
    phone: '',
    email: '',
    address: ''
  });

  // Facture details
  const [factureDetails, setFactureDetails] = useState({
    code: '',
    number: '',
    date: new Date().toISOString().split('T')[0], // Current date in YYYY-MM-DD format
    sub_total: 0,
    total: 0,
    amount_paid: 0,
    amount_due: 0,
    timbre: 1 // Timbre fiscal (DT), suggested 1.000 but OPTIONAL — can be set to 0
  });

  // Payment-method step (modal shown after pressing "Enregistrer la facture")
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('Espèces');
  // Retour fournisseur = « Facture AVOIR » (bouton « Retour fournisseur » à
  // côté du titre) : les produits SORTENT du stock et le montant est déduit de
  // ce qu'on doit au fournisseur. ?avoir=1 ouvre directement en mode avoir.
  const [isAvoir, setIsAvoir] = useState(() => new URLSearchParams(window.location.search).get('avoir') === '1');
  // Remise % de l'avoir papier (« Remise % 5,000 ») appliquée à toutes les lignes.
  const [avoirRemise, setAvoirRemise] = useState(0);

  // Products section
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [productSearch, setProductSearch] = useState('');
  const [showProductDropdown, setShowProductDropdown] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [editingProductId, setEditingProductId] = useState(null); // unique_id of line being edited via pencil

  // Carte « Nouveau produit » — même fiche complète que la carte « Nouveau
  // produit reçu » de la réception d'un bon de commande (CommandeDetail) :
  // drafts string liés achat TTC ⇄ marge ⇄ vente, TVA/marge société par défaut.
  const [createPop, setCreatePop] = useState(null);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [laboratoires, setLaboratoires] = useState([]);
  const { tva: companyTva, marge: companyMarge } = useCompany();

  // TVA rate (default 19%)
  const TVA_RATE = 19;

  // Fetch suppliers and products on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch suppliers
        const suppliersData = await supplierService.getAllSuppliers();
        console.log('Suppliers data:', suppliersData); // Debug log
        setSuppliers(Array.isArray(suppliersData) ? suppliersData : []);
        setFilteredSuppliers(Array.isArray(suppliersData) ? suppliersData : []);

        // Fetch products
        const productsData = await productService.getAllProducts();
        console.log('Products data:', productsData); // Debug log
        setProducts(Array.isArray(productsData) ? productsData : []);

        // Fetch categories
        const categoriesResponse = await categoryService.getAllCategories();
        const categoriesData = categoriesResponse.results || categoriesResponse || [];
        console.log('Categories data:', categoriesData); // Debug log
        setCategories(Array.isArray(categoriesData) ? categoriesData : []);

        // Fetch subcategories
        const subcategoriesResponse = await subcategoryService.getAllSubcategories();
        const subcategoriesData = subcategoriesResponse.results || subcategoriesResponse || [];
        console.log('Subcategories data:', subcategoriesData); // Debug log
        setSubcategories(Array.isArray(subcategoriesData) ? subcategoriesData : []);

        // Fetch laboratoires (fiche produit complète)
        const labosResponse = await laboratoireService.getAllLaboratoires().catch(() => []);
        const labosData = labosResponse.results || labosResponse || [];
        setLaboratoires(Array.isArray(labosData) ? labosData : []);
      } catch (error) {
        console.error('Error fetching initial data:', error);
        Swal.fire('Error', 'Failed to load initial data', 'error');
      }
    };

    fetchData();
  }, []);

  // Pré-remplissage depuis un bon de commande (réception de marchandise).
  useEffect(() => {
    if (!commandePrefill) return;
    setSelectedSupplier({
      id: commandePrefill.supplier.id,
      name: commandePrefill.supplier.name,
      phone: commandePrefill.supplier.phone || '',
    });
    setSelectedProducts((commandePrefill.items || []).flatMap((item, index) => {
      const lines = [];
      if (parseInt(item.quantity || 0) > 0) {
        lines.push({
          id: item.product,
          unique_id: Date.now().toString() + index,
          name: item.product_name,
          cost: parseFloat(item.expected_cost || 0),
          original_cost: parseFloat(item.expected_cost || 0),
          quantity: parseInt(item.quantity || 1),
          tva_rate: 0,
          is_discount: false,
          is_free: false,
          discount_percentage: 0,
          total: parseFloat(item.expected_cost || 0) * parseInt(item.quantity || 1),
        });
      }
      // Unités gratuites du BC → ligne gratuite de la facture (même forme
      // qu'un is_free coché à la main : coût 0, remise 100 %).
      if (parseInt(item.free_quantity || 0) > 0) {
        lines.push({
          id: item.product,
          unique_id: `${Date.now().toString()}${index}-free`,
          name: item.product_name,
          cost: 0,
          original_cost: parseFloat(item.expected_cost || 0),
          quantity: parseInt(item.free_quantity),
          tva_rate: 0,
          is_discount: false,
          is_free: true,
          discount_percentage: 100,
          total: 0,
        });
      }
      return lines;
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load facture data if in edit mode
  useEffect(() => {
    const loadFacture = async () => {
      if (!isEditMode || !id) return;

      try {
        setLoadingFacture(true);
        const factureData = await factureService.getFactureById(id);
        console.log('Loaded facture data:', factureData);

        // Set facture details
        setFactureDetails({
          code: factureData.barrecode || '',
          number: factureData.number || '',
          date: factureData.date_added ? factureData.date_added.split('T')[0] : new Date().toISOString().split('T')[0],
          sub_total: factureData.sub_total || 0,
          total: factureData.total || 0,
          amount_paid: factureData.amount_paid || 0,
          amount_due: factureData.amount_due || 0,
          timbre: factureData.timbre != null ? factureData.timbre : 1,
          livraison: factureData.livraison || ''
        });
        if (factureData.payment_method) setPaymentMethod(factureData.payment_method);
        setIsAvoir(!!factureData.is_avoir);

        // Set supplier
        if (factureData.supplier) {
          const supplier = suppliers.find(s => s.id === factureData.supplier);
          if (supplier) {
            setSelectedSupplier(supplier);
          } else {
            // If supplier not in the list yet, create a minimal supplier object
            setSelectedSupplier({
              id: factureData.supplier,
              name: factureData.supplier_name || 'Supplier',
              phone: factureData.supplier_phone || ''
            });
          }
        }

        // Set products
        if (factureData.details && Array.isArray(factureData.details)) {
          const loadedProducts = factureData.details.map((detail, index) => {
            const cost = parseFloat(detail.cost || 0);
            const pct = parseFloat(detail.discount_percentage || 0);
            // Prix AVANT remise = original_cost stocké ; à défaut (ancienne
            // ligne sans remise persistée) le coût enregistré. Ne JAMAIS
            // remettre le coût remisé en original_cost : c'est ce qui faisait
            // perdre la remise à chaque modification.
            const original = (detail.original_cost != null && detail.original_cost !== '')
              ? parseFloat(detail.original_cost) : cost;
            return {
              id: detail.product,
              unique_id: Date.now().toString() + index,
              name: detail.product_name,
              cost,
              original_cost: original,
              quantity: (Math.abs(parseInt(detail.quantity || 1)) || 1),
              // Timbre lines stay at 0% even if a legacy row stored otherwise.
              tva_rate: isTimbre(detail.product_name) ? 0 : parseFloat(detail.tva || 0),
              // Ancienne ligne « remisée » sans % stocké : l'info est perdue —
              // on repart sans remise plutôt que d'en afficher une fausse.
              is_discount: !!detail.is_discount && pct > 0,
              is_free: detail.is_free || false,
              discount_percentage: pct,
              total: cost * (Math.abs(parseInt(detail.quantity || 1)) || 1)
            };
          });
          setSelectedProducts(loadedProducts);
        }

        setLoadingFacture(false);
      } catch (error) {
        console.error('Error loading facture:', error);
        Swal.fire('Erreur', 'Impossible de charger la facture', 'error');
        setLoadingFacture(false);
        navigate('/facturation/list');
      }
    };

    loadFacture();
  }, [id, isEditMode, suppliers]); // Depend on suppliers so we can set the selected supplier

  // Filter suppliers based on search input
  // Règle Ki Para (utils/search.js) : début de mot sur le nom, téléphone
  // « contient », « commence par » en tête.
  useEffect(() => {
    setFilteredSuppliers(filterSearch(suppliers, supplierSearch, {
      text: (s) => s.name, codes: (s) => [s.phone],
    }));
  }, [supplierSearch, suppliers]);

  // Filter products based on search input
  useEffect(() => {
    if (!productSearch.trim()) {
      setFilteredProducts([]);
      setShowProductDropdown(false);
    } else {
      // Règle Ki Para (utils/search.js) : début de mot sur le nom, code /
      // code-barres « contient », « commence par » en tête.
      const filtered = filterSearch(products, productSearch, {
        text: (p) => p.name, codes: (p) => [p.code, p.barcode],
      });
      setFilteredProducts(filtered);
      setShowProductDropdown(true);
    }
  }, [productSearch, products]);

  // Handle supplier selection
  const handleSelectSupplier = (supplier) => {
    setSelectedSupplier(supplier);
    setSupplierSearch('');
    setShowSupplierDropdown(false);
  };

  // Handle product selection
  const handleSelectProduct = (product) => {
    // Always add as a new product entry, allowing same product multiple times for different scenarios
    // Generate a unique identifier for this product instance
    const uniqueId = Date.now().toString();
    // Use product cost (prix d'achat) if available, otherwise use price (prix de vente)
    const productCost = product.cost || product.price || 0;
    // Use product's TVA if available, otherwise use default TVA_RATE.
    // Exception: the timbre fiscal is NOT subject to TVA — it IS a tax, so it
    // is never taxed again. A timbre line is pinned at 0% (see isTimbre()).
    const productTVA = isTimbre(product.name) ? 0 : (product.taux_tva || product.tva || TVA_RATE);

    // Create the new product entry with calculated total
    const newProduct = {
      id: product.id,           // Original product ID from database
      unique_id: uniqueId,      // Unique identifier for this particular selection
      name: product.name,
      barcode: product.barcode,
      image: product.image || null, // vignette dans le tableau (owner Studio 2026-08-13)
      cost: productCost,        // Use product cost (prix d'achat) as initial cost
      original_cost: productCost,
      quantity: 1,
      is_discount: false,
      is_free: false,
      discount_percentage: 0,
      tva_rate: productTVA,    // Use product's TVA or default 19%
      total: productCost        // Initial total is just the cost
    };

    if (isAvoir && avoirRemise > 0) {
      newProduct.is_discount = true;
      newProduct.discount_percentage = avoirRemise;
      newProduct.cost = round3(productCost * (1 - avoirRemise / 100));
      newProduct.total = newProduct.cost;
    }

    // Update products with the new entry
    const updatedProducts = [...selectedProducts, newProduct];
    setSelectedProducts(updatedProducts);

    // Clear search
    setProductSearch('');
    setShowProductDropdown(false);

    // Open the "Modifier la ligne" editor immediately for the just-added line
    setEditingProductId(uniqueId);

    // Calculate new totals immediately with TVA
    let subTotal = 0;
    let totalTVA = 0;
    let total = 0;

    updatedProducts.forEach(p => {
      const productHT = p.cost * p.quantity;
      const productTVAAmount = productHT * ((p.tva_rate || 0) / 100);

      p.total_ht = productHT;
      p.tva_amount = productTVAAmount;
      p.total = productHT + productTVAAmount;

      subTotal += productHT;
      totalTVA += productTVAAmount;
    });

    total = round3(subTotal + totalTVA);

    // Update facturation totals immediately
    setFactureDetails(prev => ({
      ...prev,
      sub_total: round3(subTotal),
      total_tva: round3(totalTVA),
      total: total,
      amount_due: round3(Math.max(0, total + (parseFloat(prev.timbre) || 0) - (prev.amount_paid || 0)))
    }));
  };

  // Calculate facture totals
  const calculateTotals = () => {
    let subTotal = 0;
    let totalTVA = 0;
    let total = 0;

    selectedProducts.forEach(product => {
      // Calculate product total based on cost (already reflects discounts or free status)
      const productTotal = product.cost * product.quantity;
      const productTVA = productTotal * (product.tva_rate / 100);

      // Update the product's total property (including TVA)
      product.total_ht = productTotal;
      product.tva_amount = productTVA;
      product.total = productTotal + productTVA;

      // Add to invoice totals
      subTotal += productTotal;
      totalTVA += productTVA;
    });

    total = round3(subTotal + totalTVA);

    setFactureDetails(prev => ({
      ...prev,
      sub_total: round3(subTotal),
      total_tva: round3(totalTVA),
      total: total,
      amount_due: round3(Math.max(0, total + (parseFloat(prev.timbre) || 0) - (prev.amount_paid || 0)))
    }));
  };

  // Handle facture details change
  const handleFactureDetailsChange = (e) => {
    const { name, value } = e.target;

    let newValue = value;
    if (name === 'amount_paid') {
      // Convert to number for calculations (au millime)
      newValue = round3(parseFloat(value) || 0);

      // Calculate current total from selected products to ensure accuracy
      const currentTotal = round3(selectedProducts.reduce(
        (sum, product) => sum + (product.total || (product.cost * product.quantity)), 0));

      // Update amount_paid and recalculate amount_due based on actual product
      // totals; le reste à payer inclut le timbre (comme le total général).
      setFactureDetails(prev => ({
        ...prev,
        [name]: newValue,
        total: currentTotal, // Ensure total is accurate
        amount_due: round3(Math.max(0, currentTotal + (parseFloat(prev.timbre) || 0) - newValue))
      }));
      return;
    }

    setFactureDetails(prev => ({
      ...prev,
      [name]: newValue
    }));
  };

  // Handle changes to selected products
  const handleProductChange = (uniqueId, field, value) => {
    const productIndex = selectedProducts.findIndex(p => p.unique_id === uniqueId);

    if (productIndex === -1) return; // Product not found

    // Create a deep copy of the selected products array
    const updatedProducts = JSON.parse(JSON.stringify(selectedProducts));
    const product = updatedProducts[productIndex];

    if (field === 'is_free' && value === true) {
      // If marking as free, set cost to 0
      product.is_free = true;
      product.is_discount = false;
      product.discount_percentage = 100;
      product.cost = 0;
    } else if (field === 'is_free' && value === false) {
      // If un-marking as free, restore original cost
      product.is_free = false;
      product.discount_percentage = 0;
      product.cost = product.original_cost;
    } else if (field === 'is_discount') {
      // Toggle discount status
      product.is_discount = value;

      if (!value) {
        // If disabling discount, restore original cost
        product.cost = product.original_cost;
        product.discount_percentage = 0;
      } else {
        // If enabling discount, apply default 50% discount if no percentage set yet
        if (!product.discount_percentage) {
          product.discount_percentage = 50;
          product.cost = product.original_cost * 0.5;
        } else {
          // Apply existing discount percentage
          product.cost = product.original_cost * (1 - (product.discount_percentage / 100));
        }
        // Can't be both free and discounted
        product.is_free = false;
      }
    } else if (field === 'discount_percentage') {
      // Apply discount percentage
      const percentage = Math.min(100, Math.max(0, parseFloat(value) || 0)); // Clamp between 0 and 100
      product.discount_percentage = percentage;

      if (percentage === 100) {
        // If 100% discount, mark as free
        product.is_free = true;
        product.is_discount = false;
        product.cost = 0;
      } else if (percentage > 0) {
        // Apply partial discount
        product.is_discount = true;
        product.is_free = false;
        product.cost = product.original_cost * (1 - (percentage / 100));
      } else {
        // No discount
        product.is_discount = false;
        product.cost = product.original_cost;
      }
    } else if (field === 'original_cost') {
      // If changing original cost
      const newCost = parseFloat(value) || 0;
      product.original_cost = newCost;

      // Recalculate cost based on discount/free status
      if (product.is_free) {
        product.cost = 0;
      } else if (product.is_discount) {
        product.cost = newCost * (1 - (product.discount_percentage / 100));
      } else {
        product.cost = newCost;
      }
    } else if (field === 'quantity') {
      // Allow the field to be cleared while typing; coerced back to >=1 on blur.
      product.quantity = value === '' || value === null ? '' : Math.max(1, parseInt(value) || 1);
    } else if (field === 'tva_rate') {
      // Update TVA rate, ensure it's between 0 and 100.
      // A timbre line is pinned at 0% — it is a tax itself and is never taxed
      // again. Without this the displayed total (and therefore the amount the
      // user pays) would include a phantom TVA that the backend never stores.
      product.tva_rate = isTimbre(product.name)
        ? 0
        : Math.min(100, Math.max(0, parseFloat(value) || 0));
    } else {
      // Update any other field
      product[field] = value;
    }

    // Calculate product total (HT)
    const totalHT = product.cost * product.quantity;
    const tvaAmount = totalHT * ((product.tva_rate || 0) / 100);
    product.total_ht = totalHT;
    product.tva_amount = tvaAmount;
    product.total = totalHT + tvaAmount;

    // Update products array
    setSelectedProducts(updatedProducts);

    // Calculate new totals directly instead of using calculateTotals
    let subTotal = 0;
    let totalTVA = 0;
    let total = 0;

    updatedProducts.forEach(p => {
      // Calculate totals with TVA for each product
      const productHT = p.cost * p.quantity;
      const productTVA = productHT * ((p.tva_rate || 0) / 100);

      // Update product totals if not already calculated
      if (p.unique_id !== uniqueId) {
        p.total_ht = productHT;
        p.tva_amount = productTVA;
        p.total = productHT + productTVA;
      }

      subTotal += productHT;
      totalTVA += productTVA;
    });

    total = round3(subTotal + totalTVA);

    // Update invoice totals immediately
    setFactureDetails(prev => ({
      ...prev,
      sub_total: round3(subTotal),
      total_tva: round3(totalTVA),
      total: total,
      amount_due: round3(Math.max(0, total + (parseFloat(prev.timbre) || 0) - (prev.amount_paid || 0)))
    }));
  };

  // Remove product from selected products
  const handleRemoveProduct = (uniqueId) => {
    // Filter out the product with the given uniqueId
    const filteredProducts = selectedProducts.filter(product => product.unique_id !== uniqueId);

    // Update state with filtered products
    setSelectedProducts(filteredProducts);

    // Calculate new totals with individual TVA rates
    let subTotal = 0;
    let totalTVA = 0;
    let total = 0;

    filteredProducts.forEach(product => {
      const productHT = product.cost * product.quantity;
      const productTVAAmount = productHT * ((product.tva_rate || 0) / 100);

      subTotal += productHT;
      totalTVA += productTVAAmount;
    });

    total = round3(subTotal + totalTVA);

    // Update invoice totals immediately
    setFactureDetails(prev => ({
      ...prev,
      sub_total: round3(subTotal),
      total_tva: round3(totalTVA),
      total: total,
      amount_due: round3(Math.max(0, total + (parseFloat(prev.timbre) || 0) - (prev.amount_paid || 0)))
    }));
  };

  // Handle adding new supplier
  const handleNewSupplierChange = (e) => {
    const { name, value } = e.target;
    setNewSupplier(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAddNewSupplier = async (e) => {
    e.preventDefault();

    if (!newSupplier.name.trim()) {
      Swal.fire('Error', 'Supplier name is required', 'error');
      return;
    }

    try {
      setLoading(true);
      const result = await supplierService.createSupplier(newSupplier);

      // Add the new supplier to the list and select it
      setSuppliers(prev => [...prev, result]);
      setSelectedSupplier(result);

      // Reset form and close it
      setNewSupplier({
        name: '',
        phone: '',
        email: '',
        address: ''
      });
      setShowAddSupplier(false);

      Swal.fire('Success', 'Supplier added successfully', 'success');
    } catch (error) {
      console.error('Error adding supplier:', error);
      Swal.fire('Error', 'Failed to add supplier', 'error');
    } finally {
      setLoading(false);
    }
  };

  // ----- Carte « Nouveau produit » (fiche complète, comme la réception d'un
  // bon de commande). Le prix d'achat est saisi TTC — le HT (fiche) se calcule
  // via la TVA société, le prix de vente est lié à la marge.
  const parseNum = (v) => parseFloat(String(v).replace(',', '.'));

  const openCreatePop = (name) => {
    setCreatePop({
      name: name || '',
      code: '',
      description: '',
      category: '',
      subcategory: '',
      laboratoire: '',
      stockMin: '0',
      imageFile: null,
      imagePreview: null,
      achat: '',
      marge: String(companyMarge),
      vente: '',
    });
  };

  // « + Nouveau » sur le champ Laboratoire de la carte : crée le labo à la
  // volée puis le sélectionne dans la fiche.
  const addNewLabo = async () => {
    const { value: laboName } = await Swal.fire({
      title: 'Nouveau laboratoire',
      input: 'text',
      inputLabel: 'Nom du laboratoire',
      inputPlaceholder: 'Ex : Laboratoire XYZ',
      showCancelButton: true,
      confirmButtonText: 'Créer',
      cancelButtonText: 'Annuler',
      inputValidator: (v) => (!v || !v.trim() ? 'Entrez le nom du laboratoire.' : undefined),
    });
    if (!laboName) return;
    try {
      const created = await laboratoireService.createLaboratoire({ name: laboName.trim() });
      setLaboratoires((prev) => [...prev, created]);
      setCreatePop((c) => (c ? { ...c, laboratoire: String(created.id) } : c));
      Swal.fire({
        toast: true, position: 'top-end', icon: 'success',
        title: `Laboratoire créé — ${created.name}`, timer: 2000, showConfirmButton: false,
      });
    } catch (e) {
      console.error('Erreur création laboratoire:', e);
      // Message de champ DRF (ex: « Un laboratoire avec ce nom existe déjà. »).
      let message = e && e.message;
      if (!message && e && typeof e === 'object') {
        const first = Object.values(e)[0];
        message = Array.isArray(first) ? first[0] : first;
      }
      Swal.fire('Erreur', message || 'Échec de la création du laboratoire', 'error');
    }
  };

  // Liaison achat TTC ⇄ marge ⇄ vente de la carte création.
  const setCreateAchat = (value) => setCreatePop((prev) => {
    const achat = parseNum(value);
    const marge = parseNum(prev.marge);
    const vente = (Number.isFinite(achat) && Number.isFinite(marge))
      ? (achat * (1 + marge / 100)).toFixed(3)
      : prev.vente;
    return { ...prev, achat: value, vente };
  });
  const setCreateMarge = (value) => setCreatePop((prev) => {
    const achat = parseNum(prev.achat);
    const marge = parseNum(value);
    const vente = (Number.isFinite(achat) && Number.isFinite(marge))
      ? (achat * (1 + marge / 100)).toFixed(3)
      : prev.vente;
    return { ...prev, marge: value, vente };
  });
  const setCreateVente = (value) => setCreatePop((prev) => {
    const achat = parseNum(prev.achat);
    const vente = parseNum(value);
    const marge = (Number.isFinite(achat) && achat > 0 && Number.isFinite(vente))
      ? (((vente - achat) / achat) * 100).toFixed(1)
      : prev.marge;
    return { ...prev, vente: value, marge };
  });

  // Crée le produit (fiche : coût HT = TTC / (1 + TVA société)) puis l'ajoute
  // directement à la facture — le stock viendra de cette facture.
  const saveCreate = async () => {
    if (!createPop || createPop.saving) return;
    const name = (createPop.name || '').trim();
    if (!name) return;
    const achatTTC = parseNum(createPop.achat);
    const ht = (Number.isFinite(achatTTC) && achatTTC > 0)
      ? achatTTC / (1 + (companyTva || 0) / 100)
      : 0;
    const vente = parseNum(createPop.vente);
    setCreatePop((c) => ({ ...c, saving: true }));
    try {
      let created;
      if (createPop.imageFile) {
        // Multipart (photo choisie) — mêmes champs que la page Ajouter un produit.
        const fd = new FormData();
        fd.append('name', name);
        fd.append('code', createPop.code || '');
        fd.append('description', createPop.description || '');
        fd.append('cost', +ht.toFixed(3));
        fd.append('price', Number.isFinite(vente) && vente >= 0 ? +vente.toFixed(3) : 0);
        fd.append('remise', 0);
        fd.append('stock', 0);
        fd.append('stock_min', parseInt(createPop.stockMin, 10) || 0);
        if (createPop.category) fd.append('category', createPop.category);
        if (createPop.subcategory) fd.append('subcategory', createPop.subcategory);
        if (createPop.laboratoire) fd.append('laboratoire', createPop.laboratoire);
        fd.append('image', createPop.imageFile);
        created = await productService.createProductForm(fd);
      } else {
        created = await productService.createProduct({
          name,
          code: createPop.code || null,
          description: createPop.description || null,
          cost: +ht.toFixed(3),
          price: Number.isFinite(vente) && vente >= 0 ? +vente.toFixed(3) : 0,
          remise: 0,
          stock: 0,
          stock_min: parseInt(createPop.stockMin, 10) || 0,
          category: createPop.category || null,
          subcategory: createPop.subcategory || null,
          laboratoire: createPop.laboratoire || null,
        });
      }
      setProducts((prev) => [...prev, created]);
      setCreatePop(null);
      // Ajoute le produit créé comme ligne de la facture (ouvre le crayon).
      handleSelectProduct(created);
      Swal.fire({
        toast: true, position: 'top-end', icon: 'success',
        title: `Produit créé — ${name}`, timer: 2200, showConfirmButton: false,
      });
    } catch (e) {
      console.error('Erreur création produit:', e);
      setCreatePop((c) => (c ? { ...c, saving: false } : c));
      const message = extractApiErrorMessage(e);
      // Nom en doublon → carte « Produit déjà au catalogue » (brand kit) :
      // proposer d'ajouter la fiche existante comme ligne de la facture.
      if (isDuplicateProductNameError(message)) {
        const existing = products.find((p) => normProductName(p.name) === normProductName(name)) || null;
        duplicateProductAlert({
          product: existing, name,
          confirmText: existing ? 'Utiliser ce produit' : undefined,
        }).then((r) => {
          if (r.isConfirmed && existing) {
            setCreatePop(null);
            handleSelectProduct(existing);
          }
        });
        return;
      }
      Swal.fire('Erreur', message || 'Échec de la création du produit', 'error');
    }
  };

  // Save facture
  const handleSaveFacture = async () => {
    // Validation
    if (!selectedSupplier) {
      Swal.fire('Erreur', 'Veuillez sélectionner un fournisseur', 'error');
      return;
    }

    // Code is now optional

    if (selectedProducts.length === 0) {
      Swal.fire('Erreur', 'Veuillez ajouter au moins un produit', 'error');
      return;
    }

    if (isAvoir) { await handleSaveAvoir(); return; }

    try {
      // Parse code and number as integers - code is now optional
      const barrecodeInt = factureDetails.code ? parseInt(factureDetails.code) : 0;
      if (factureDetails.code && isNaN(barrecodeInt)) {
        Swal.fire('Erreur', 'Le code de facture doit être un nombre', 'error');
        return;
      }

      const factureNumber = parseInt(factureDetails.number || 0);
      if (isNaN(factureNumber)) {
        Swal.fire('Erreur', 'Le numéro de facture doit être un nombre', 'error');
        return;
      }

      // Prepare updated facture data format for backend API.
      // Use the freshly-derived totals so the timbre is included in the grand total.
      // Le montant payé ne peut jamais dépasser le total de la facture.
      const paid = Math.min(round3(factureDetails.amount_paid || 0), grandTotal);
      const factureData = {
        barrecode: barrecodeInt,
        number: factureNumber,
        date: factureDetails.date, // Include the date
        supplier: selectedSupplier.id,
        sub_total: subTotalHT,
        total: grandTotal, // Total TTC + timbre
        amount_paid: paid,
        amount_due: round3(Math.max(0, grandTotal - paid)),
        timbre: timbreVal,
        payment_method: paymentMethod,
        // Frais de livraison — hors total facture, comptés comme charge.
        livraison: round3(Math.max(0, parseFloat(factureDetails.livraison) || 0)),
        products: selectedProducts.map(product => ({
          id: product.id,
          cost: product.is_free ? 0 : (product.is_discount ?
            round3(product.original_cost * (1 - product.discount_percentage / 100)) :
            round3(product.original_cost)),
          // Prix AVANT remise — persisté par le backend pour que la remise
          // survive à l'affichage et aux modifications.
          original_cost: round3(product.original_cost || 0),
          quantity: product.quantity,
          tva: product.tva_rate || 0,
          is_discount: product.is_discount,
          is_free: product.is_free,
          discount_percentage: product.discount_percentage || 0
        }))
      };

      console.log('Saving facture:', factureData);

      // Qui enregistre ? La personne donne son code : la
      // facture bouge le stock et l'argent. Annulé = rien n'est enregistré.
      const who = await requestIdentity({ title: 'Qui enregistre la facture ?' });
      if (who === false) return;

      // Call API to create or update facture
      await withIdentity(who, async () => {
      if (isEditMode) {
        await factureService.updateFacture(id, factureData);
        setShowPaymentModal(false);
        Swal.fire('Succès', 'Facture modifiée avec succès', 'success');
      } else {
        const created = await factureService.createFacture(factureData);
        setShowPaymentModal(false);
        // Réception d'un bon de commande : lier la facture créée et passer
        // le BC à « Reçu ». La facture reste valide même si le lien échoue.
        if (commandePrefill?.id) {
          try {
            await commandeService.markReceived(commandePrefill.id, created?.id || null);
            Swal.fire('Succès', `Facture enregistrée — BC #${commandePrefill.number} marqué comme reçu`, 'success');
          } catch (linkError) {
            console.error('Facture créée mais échec du lien avec le bon de commande:', linkError);
            Swal.fire('Succès', 'Facture enregistrée (mais le bon de commande n\'a pas pu être marqué reçu)', 'success');
          }
        } else {
          Swal.fire('Succès', 'Facture enregistrée avec succès', 'success');
        }
      }
      });

      // Un compte vendeur sans « Accès facturation » ajoute des factures mais
      // n'ouvre pas la liste : il revient à l'accueil.
      navigate(commandePrefill?.id ? '/commandes' : (authService.hasFacturationAccess() ? '/facturation/list' : '/'));
    } catch (error) {
      console.error('Error saving facture:', error.response?.data || error);
      // Show more specific error if available
      const errorMsg = error.response?.data?.error || `Échec de ${isEditMode ? 'la modification' : 'l\'enregistrement'} de la facture`;
      Swal.fire('Erreur', errorMsg, 'error');
    }
  };

  // ===== Retour fournisseur (Facture AVOIR) =====
  const applyAvoirRemise = (value) => {
    let pct = parseFloat(value);
    if (isNaN(pct) || pct < 0) pct = 0;
    if (pct > 100) pct = 100;
    setAvoirRemise(pct);
    setSelectedProducts((prev) => prev.map((p) => (p.is_free ? p : {
      ...p,
      is_discount: pct > 0,
      discount_percentage: pct,
      cost: round3((p.original_cost || 0) * (1 - pct / 100)),
    })));
  };

  // Pilule « Facture | Retour » (même kit .segp que Produits | Services de
  // Nouvelle vente). En changeant de type, la remise « toutes les lignes » de
  // l'avoir est retirée des lignes qui la portent ; une remise mise à la main
  // sur une ligne (autre %) est gardée.
  const setAvoirMode = (on) => {
    if (on === isAvoir) return;
    if (avoirRemise > 0) {
      setSelectedProducts((prev) => prev.map((p) => (
        !p.is_free && p.is_discount && Number(p.discount_percentage) === Number(avoirRemise)
          ? { ...p, is_discount: false, discount_percentage: 0, cost: round3(p.original_cost || 0) }
          : p)));
    }
    setAvoirRemise(0);
    setIsAvoir(on);
  };

  const escHtml = (t) => String(t ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

  const handleSaveAvoir = async () => {
    const barrecodeInt = factureDetails.code ? parseInt(factureDetails.code) : 0;
    const factureNumber = parseInt(factureDetails.number || 0);
    if ((factureDetails.code && isNaN(barrecodeInt)) || isNaN(factureNumber)) {
      Swal.fire('Erreur', 'Le code et le numéro de l’avoir doivent être des nombres', 'error');
      return;
    }
    const amount = grandTotal; // montant de l'avoir (positif dans le formulaire)
    const due = parseFloat(selectedSupplier.total_amount_due) || 0;
    const used = round3(Math.min(due, amount));
    const over = selectedProducts.filter((p) => {
      const prod = products.find((x) => x.id === p.id);
      return prod && (parseInt(p.quantity) || 0) > (prod.stock || 0);
    });
    const ok = await Swal.fire({
      title: isEditMode ? 'Modifier le retour ?' : 'Enregistrer le retour ?',
      html: `<div style="text-align:left;font-size:14px">Fournisseur : <b>${escHtml(selectedSupplier.name)}</b> · Avoir N° ${escHtml(factureDetails.number || '—')}`
        + `<ul style="margin:8px 0 8px 18px;list-style:disc">${selectedProducts.map((p) => `<li>${p.quantity} × ${escHtml(p.name)}</li>`).join('')}</ul>`
        + `Total avoir : <b>${amount.toFixed(3)} DT</b><br/>`
        + (isEditMode ? '' : `Déduit de ce que vous devez : <b>${used.toFixed(3)} DT</b>`
          + (amount - used > 0.0005 ? `<br/>Reste d’avoir : <b>${(amount - used).toFixed(3)} DT</b> (déduit des prochaines factures)` : ''))
        + `<p style="margin-top:8px;color:${KI.muted}">Ces produits sortent du stock.</p>`
        + (over.length ? `<p style="margin-top:6px;color:${KI.goldText}"><b>Attention :</b> ${over.map((p) => escHtml(p.name)).join(', ')} — plus que le stock actuel.</p>` : '')
        + '</div>',
      showCancelButton: true,
      confirmButtonText: 'Enregistrer l’avoir',
      cancelButtonText: 'Annuler',
      reverseButtons: true
    });
    if (!ok.isConfirmed) return;

    const factureData = {
      barrecode: barrecodeInt,
      number: factureNumber,
      date: factureDetails.date,
      supplier: selectedSupplier.id,
      is_avoir: true,
      amount_paid: 0,
      timbre: 0,
      livraison: 0,
      payment_method: '',
      products: selectedProducts.map((product) => ({
        id: product.id,
        cost: product.is_free ? 0 : (product.is_discount
          ? round3(product.original_cost * (1 - product.discount_percentage / 100))
          : round3(product.original_cost)),
        original_cost: round3(product.original_cost || 0),
        quantity: Math.abs(parseInt(product.quantity) || 1), // le serveur les passe en négatif
        tva: product.tva_rate || 0,
        is_discount: product.is_discount,
        is_free: product.is_free,
        discount_percentage: product.discount_percentage || 0
      }))
    };

    const who = await requestIdentity({ title: 'Qui enregistre le retour ?' });
    if (who === false) return;
    try {
      const saved = await withIdentity(who, () => (isEditMode
        ? factureService.updateFacture(id, factureData)
        : factureService.createFacture(factureData)));
      const applied = round3((saved?.avoir_detail || []).reduce((sum, d) => sum + (parseFloat(d.amount) || 0), 0));
      const left = round3(parseFloat(saved?.avoir_remaining) || 0);
      Swal.fire({
        title: isEditMode ? 'Avoir modifié' : 'Avoir enregistré',
        html: `Déduit des factures de ${escHtml(selectedSupplier.name)} : <b>${applied.toFixed(3)} DT</b>`
          + (left > 0 ? `<br/>Reste d’avoir : <b>${left.toFixed(3)} DT</b> — déduit des prochaines factures.` : ''),
        icon: 'success'
      });
      navigate(authService.hasFacturationAccess() ? '/facturation/list' : '/');
    } catch (error) {
      console.error('Error saving avoir:', error.response?.data || error);
      Swal.fire('Erreur', error.response?.data?.error || 'Échec de l’enregistrement de l’avoir', 'error');
    }
  };

  // Derived totals for the financial summary sidebar
  const subTotalHT = round3(selectedProducts.reduce((sum, p) => sum + (p.cost * p.quantity), 0));
  const totalTVA = round3(selectedProducts.reduce((sum, p) => sum + (p.cost * p.quantity) * ((p.tva_rate || 0) / 100), 0));
  const totalTTC = round3(subTotalHT + totalTVA);
  const timbreVal = isAvoir ? 0 : round3(parseFloat(factureDetails.timbre) || 0); // pas de timbre sur un avoir
  const sgn = isAvoir ? -1 : 1; // l'avoir s'affiche en négatif, comme le papier
  const grandTotal = round3(totalTTC + timbreVal);

  // The product line currently open in the edit modal (pencil)
  const editingProduct = selectedProducts.find(p => p.unique_id === editingProductId);

  return (
    <div className="w-full px-4 pt-2 pb-4">
      {/* Contexte réception d'un bon de commande */}
      {commandePrefill && (
        <div className="mb-4 flex items-center gap-3 bg-green-50 border border-green-200 text-green-800 rounded-xl px-4 py-3 text-sm">
          <IcPackageOpen className="shrink-0" />
          <span>
            Réception du <b>BC #{commandePrefill.number}</b> ({commandePrefill.supplier?.name}) —
            vérifiez les quantités et les <b>prix réels</b>, puis enregistrez la facture.
            Le bon de commande passera automatiquement à « Reçu ».
          </span>
        </div>
      )}
      {/* ===== Carte « Nouveau produit » : fiche complète sur place — même
          carte que « Nouveau produit reçu » de la réception d'un bon de
          commande (CommandeDetail). Le prix d'achat est saisi TTC — HT
          (fiche) via la TVA société, prix de vente précalculé à la marge
          société. ===== */}
      {createPop && createPortal(
        (() => {
          const achatNum = parseFloat(String(createPop.achat).replace(',', '.'));
          const htNum = Number.isFinite(achatNum) ? achatNum / (1 + (companyTva || 0) / 100) : null;
          const margeNum = parseFloat(createPop.marge);
          const subsFiltered = createPop.category
            ? subcategories.filter((s) => String(s.category) === String(createPop.category) || String(s.category_id) === String(createPop.category))
            : subcategories;
          const inputCls = 'w-full px-3 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-1 focus:ring-gray-900 focus:border-gray-900';
          return (
            <div className="modal-backdrop z-[85] flex items-center justify-center p-4" onClick={() => setCreatePop(null)}>
              <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[92vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
                {/* Composants-2 §10 : entête aplat encre, ✕ gris → blanc */}
                <div className="modal-head py-3.5 items-start gap-3 shrink-0">
                  <div className="flex-1 min-w-0">
                    <h3 className="modal-title leading-tight flex items-center gap-2"><IcPlus size={15} /> Nouveau produit</h3>
                    <p className="text-xs text-gray-300 mt-0.5">Créer sa fiche complète et l'ajouter à la facture</p>
                  </div>
                  <button onClick={() => setCreatePop(null)} className="h-8 w-8 rounded-full flex items-center justify-center text-gray-400 hover:text-white shrink-0" aria-label="Fermer"><IcClose size={13} /></button>
                </div>

                <div className="flex-1 overflow-y-auto p-4 space-y-3">
                  {/* Photo + nom / code */}
                  <div className="flex gap-3">
                    <label className="shrink-0 h-24 w-24 rounded-xl border-2 border-dashed border-gray-200 hover:border-blue-300 bg-gray-50 flex items-center justify-center cursor-pointer overflow-hidden" title="Photo du produit">
                      {createPop.imagePreview
                        ? <img src={createPop.imagePreview} alt="" className="h-full w-full object-contain" />
                        : <span className="text-[10px] text-gray-400 font-medium text-center px-1">Photo<br />(optionnel)</span>}
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => {
                          const f = e.target.files && e.target.files[0];
                          setCreatePop((c) => ({
                            ...c,
                            imageFile: f || null,
                            imagePreview: f ? URL.createObjectURL(f) : null,
                          }));
                        }}
                      />
                    </label>
                    <div className="flex-1 space-y-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1.5">Nom du produit *</label>
                        <input
                          type="text"
                          value={createPop.name}
                          onChange={(e) => setCreatePop((c) => ({ ...c, name: e.target.value }))}
                          className={`${inputCls} font-semibold`}
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1.5">Code-barres</label>
                        <input
                          type="text"
                          value={createPop.code}
                          onChange={(e) => setCreatePop((c) => ({ ...c, code: e.target.value }))}
                          placeholder="Scanner ou saisir…"
                          className={`${inputCls} tabular-nums`}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Catégorie / sous-catégorie / laboratoire / stock min */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1.5">Catégorie</label>
                      <select
                        value={createPop.category}
                        onChange={(e) => setCreatePop((c) => ({ ...c, category: e.target.value, subcategory: '' }))}
                        className={inputCls}
                      >
                        <option value="">— Aucune —</option>
                        {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1.5">Sous-catégorie</label>
                      <select
                        value={createPop.subcategory}
                        onChange={(e) => setCreatePop((c) => ({ ...c, subcategory: e.target.value }))}
                        className={inputCls}
                      >
                        <option value="">— Aucune —</option>
                        {subsFiltered.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                      </select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1.5">Laboratoire</label>
                      <select
                        value={createPop.laboratoire}
                        onChange={(e) => {
                          // « + Nouveau laboratoire… » dans la liste : ouvre
                          // l'invite de création, la valeur reste inchangée.
                          if (e.target.value === '__new__') { addNewLabo(); return; }
                          setCreatePop((c) => ({ ...c, laboratoire: e.target.value }));
                        }}
                        className={inputCls}
                      >
                        <option value="">— Aucun —</option>
                        <option value="__new__" className="font-semibold text-blue-600">+ Nouveau laboratoire…</option>
                        {laboratoires.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1.5">Stock minimum</label>
                      <input
                        type="number"
                        inputMode="numeric"
                        min="0"
                        value={createPop.stockMin}
                        onChange={(e) => setCreatePop((c) => ({ ...c, stockMin: e.target.value }))}
                        onFocus={(e) => e.target.select()}
                        className={`${inputCls} tabular-nums`}
                      />
                    </div>
                  </div>

                  {/* Prix : achat TTC (HT affiché) / marge / vente */}
                  <div className="rounded-xl border border-blue-100 bg-blue-50/40 p-3 space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1.5">Prix d'achat TTC</label>
                        <div className="relative">
                          <input
                            type="number"
                            inputMode="decimal"
                            min="0"
                            step="0.001"
                            value={createPop.achat}
                            onChange={(e) => setCreateAchat(e.target.value)}
                            onFocus={(e) => e.target.select()}
                            placeholder="0.000"
                            className="w-full pl-3 pr-12 py-2.5 border border-gray-200 bg-white rounded-xl focus:outline-none focus:ring-1 focus:ring-gray-900 focus:border-gray-900 tabular-nums font-semibold"
                          />
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-semibold text-gray-400">DT</span>
                        </div>
                        {htNum != null && (
                          <p className="text-[11px] text-gray-400 mt-1 tabular-nums">
                            HT (fiche) : <span className="font-semibold text-gray-600">{htNum.toFixed(3)}</span> — TVA {companyTva}%
                          </p>
                        )}
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1.5">Marge</label>
                        <div className="relative">
                          <input
                            type="number"
                            inputMode="decimal"
                            step="0.1"
                            value={createPop.marge}
                            onChange={(e) => setCreateMarge(e.target.value)}
                            onFocus={(e) => e.target.select()}
                            className={`w-full pl-3 pr-9 py-2.5 border border-gray-200 bg-white rounded-xl focus:outline-none focus:border-gray-900 focus:ring-1 focus:ring-gray-900 tabular-nums font-bold ${Number.isFinite(margeNum) && margeNum < 0 ? 'text-red-600' : 'text-green-700'}`}
                          />
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-semibold text-gray-400">%</span>
                        </div>
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1.5">Prix de vente</label>
                      <div className="relative max-w-[12rem]">
                        <input
                          type="number"
                          inputMode="decimal"
                          min="0"
                          step="0.001"
                          value={createPop.vente}
                          onChange={(e) => setCreateVente(e.target.value)}
                          onFocus={(e) => e.target.select()}
                          className="w-full pl-3 pr-12 py-2.5 border border-green-300 ring-1 ring-green-100 bg-white rounded-xl focus:outline-none focus:border-gray-900 focus:ring-1 focus:ring-gray-900 tabular-nums font-semibold"
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-semibold text-gray-400">DT</span>
                      </div>
                    </div>
                  </div>

                  {/* Description */}
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1.5">Description</label>
                    <textarea
                      rows="2"
                      value={createPop.description}
                      onChange={(e) => setCreatePop((c) => ({ ...c, description: e.target.value }))}
                      placeholder="Description (optionnel)…"
                      className={`${inputCls} text-sm`}
                    ></textarea>
                  </div>

                  <p className="text-[11px] text-gray-400">
                    Le produit sera créé puis ajouté à la facture — son stock viendra de cette facture.
                  </p>
                </div>

                <div className="px-4 py-3 border-t border-gray-100 shrink-0 flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => setCreatePop(null)}
                    className="px-4 py-3 text-sm btn-g transition-all"
                  >
                    Annuler
                  </button>
                  <button
                    type="button"
                    onClick={saveCreate}
                    disabled={createPop.saving || !(createPop.name || '').trim()}
                    className="px-5 py-3 text-sm btn-p transition-all"
                  >
                    {createPop.saving ? 'Création…' : 'Créer le produit'}
                  </button>
                </div>
              </div>
            </div>
          );
        })(),
        document.body
      )}

      {/* Edit product line modal (opened by the pencil) */}
      {editingProduct && (
        <div className="modal-backdrop flex items-center justify-center z-50 p-4" onClick={() => setEditingProductId(null)}>
          <div className="bg-white rounded-2xl shadow-lg p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="modal-head -mx-6 -mt-6 mb-4 rounded-t-2xl">
              <h2 className="modal-title text-lg">Modifier la ligne</h2>
              <button onClick={() => setEditingProductId(null)} className="text-gray-400 hover:text-white" aria-label="Fermer">
                <IcClose />
              </button>
            </div>
            <p className="text-sm text-gray-500 mb-5">{editingProduct.name}</p>

            <div className="space-y-4">
              {/* Prix d'achat */}
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1.5">Prix d'achat HT</label>
                <div className="relative">
                  <input
                    type="number"
                    value={editingProduct.original_cost || ''}
                    onChange={(e) => {
                      const value = e.target.value === '' ? 0 : parseFloat(e.target.value);
                      handleProductChange(editingProduct.unique_id, 'original_cost', value);
                    }}
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-gray-900 focus:border-gray-900 disabled:bg-gray-100 disabled:text-gray-400"
                    min="0"
                    step="0.001"
                    disabled={editingProduct.is_discount || editingProduct.is_free}
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">DT</span>
                </div>
              </div>

              {/* Quantité + TVA + Remis */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1.5">{isAvoir ? 'Quantité retournée' : 'Quantité'}</label>
                  <input
                    type="number"
                    value={editingProduct.quantity}
                    onChange={(e) => handleProductChange(editingProduct.unique_id, 'quantity', e.target.value)}
                    onBlur={(e) => {
                      if (e.target.value === '' || parseInt(e.target.value) < 1) {
                        handleProductChange(editingProduct.unique_id, 'quantity', 1);
                      }
                    }}
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-gray-900 focus:border-gray-900"
                    min="1"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1.5">TVA %</label>
                  <input
                    type="number"
                    value={editingProduct.tva_rate !== undefined && editingProduct.tva_rate !== null ? editingProduct.tva_rate : ''}
                    onChange={(e) => {
                      const value = e.target.value === '' ? 0 : parseFloat(e.target.value);
                      handleProductChange(editingProduct.unique_id, 'tva_rate', value);
                    }}
                    disabled={isTimbre(editingProduct.name)}
                    className={`w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-gray-900 focus:border-gray-900 ${
                      isTimbre(editingProduct.name) ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : ''
                    }`}
                    min="0"
                    max="100"
                    step="0.1"
                  />
                  {isTimbre(editingProduct.name) && (
                    <p className="mt-1 text-[11px] text-gray-500">
                      Le timbre fiscal est une taxe : il n'est jamais soumis à la TVA.
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1.5">Remise %</label>
                  <input
                    type="number"
                    value={editingProduct.discount_percentage || ''}
                    onChange={(e) => {
                      const value = e.target.value === '' ? 0 : parseFloat(e.target.value);
                      handleProductChange(editingProduct.unique_id, 'discount_percentage', value);
                    }}
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-gray-900 focus:border-gray-900 disabled:bg-gray-100 disabled:text-gray-400"
                    min="0"
                    max="100"
                    step="0.1"
                    placeholder="0"
                    disabled={editingProduct.is_free}
                  />
                </div>
              </div>

              {/* Gratuit */}
              <div className="bg-gray-50 rounded-xl p-3">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={editingProduct.is_free}
                    onChange={(e) => handleProductChange(editingProduct.unique_id, 'is_free', e.target.checked)}
                    className="form-checkbox h-4 w-4 text-blue-600 rounded"
                    disabled={editingProduct.is_discount}
                  />
                  <span className="ml-2 text-sm font-medium text-gray-700">Produit gratuit</span>
                </label>
              </div>

              {/* Line totals preview */}
              <div className="border-t border-gray-100 pt-4 space-y-1.5 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Total HT</span>
                  <span className="font-medium text-gray-800">{(editingProduct.cost * editingProduct.quantity).toFixed(3)} DT</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Montant TVA</span>
                  <span className="font-medium text-emerald-600">{((editingProduct.cost * editingProduct.quantity) * ((editingProduct.tva_rate || 0) / 100)).toFixed(3)} DT</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-semibold text-gray-700">Total TTC</span>
                  <span className="font-bold text-blue-600">{((editingProduct.cost * editingProduct.quantity) * (1 + (editingProduct.tva_rate || 0) / 100)).toFixed(3)} DT</span>
                </div>
              </div>
            </div>

            {/* Supprimer déplacé ici depuis la colonne Actions (Ahmed 2026-08-17) */}
            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={() => {
                  const id = editingProduct.unique_id;
                  setEditingProductId(null);
                  handleRemoveProduct(id);
                }}
                className="px-6 py-2.5 flex items-center justify-center gap-2 btn-db transition-colors"
              >
                <IcTrash /> Supprimer
              </button>
              <button
                onClick={() => setEditingProductId(null)}
                className="px-6 py-2.5 btn-p transition-colors"
              >
                Terminé
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Payment-method modal — opens after "Enregistrer la facture", styled like "Modifier la ligne" */}
      {showPaymentModal && (
        <div className="modal-backdrop flex items-center justify-center z-50 p-4" onClick={() => setShowPaymentModal(false)}>
          <div className="bg-white rounded-2xl shadow-lg p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="modal-head -mx-6 -mt-6 mb-4 rounded-t-2xl">
              <h2 className="modal-title text-lg">{selectedSupplier ? selectedSupplier.name : 'Méthode de paiement'}</h2>
              <button onClick={() => setShowPaymentModal(false)} className="text-gray-400 hover:text-white" aria-label="Fermer">
                <IcClose />
              </button>
            </div>
            {(() => {
              const totalQty = selectedProducts.reduce((sum, p) => sum + (parseInt(p.quantity) || 0), 0);
              return (
                <p className="text-sm text-gray-500 mb-5">
                  Facture N° {factureDetails.number || '—'} · {totalQty} produit{totalQty > 1 ? 's' : ''}
                </p>
              );
            })()}

            {/* Totals breakdown */}
            <div className="space-y-1.5 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">Total HT</span>
                <span className="font-medium text-gray-800">{subTotalHT.toFixed(3)} DT</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Total TTC</span>
                <span className="font-medium text-gray-800">{totalTTC.toFixed(3)} DT</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Timbre</span>
                <span className="font-medium text-gray-800">{timbreVal.toFixed(3)} DT</span>
              </div>
              <div className="flex justify-between pt-2 border-t border-gray-100">
                <span className="font-semibold text-gray-700">Total</span>
                <span className="text-lg font-bold text-red-600">{grandTotal.toFixed(3)} DT</span>
              </div>
            </div>

            {/* Montant payé — open input, sized like the method cases */}
            <div className="mt-5">
              <label className="block text-sm font-medium text-gray-600 mb-1.5">Montant payé</label>
              <div className="relative">
                <input
                  type="number"
                  name="amount_paid"
                  value={factureDetails.amount_paid || ''}
                  onChange={(e) => {
                    // Cap le montant payé au total de la facture (jamais plus).
                    // grandTotal est arrondi au millime, donc saisir exactement
                    // le total affiché (ex. 64.300) est accepté tel quel.
                    const capped = Math.min(round3(parseFloat(e.target.value) || 0), grandTotal);
                    handleFactureDetailsChange({ target: { name: 'amount_paid', value: capped } });
                  }}
                  className="w-full pl-4 pr-28 py-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-1 focus:ring-gray-900 focus:border-gray-900"
                  min="0"
                  max={grandTotal}
                  step="0.001"
                  placeholder="0.000"
                />
                <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-2">
                  <span className="text-gray-400 text-sm">DT</span>
                  <button
                    type="button"
                    onClick={() => handleFactureDetailsChange({ target: { name: 'amount_paid', value: grandTotal } })}
                    className="px-3 py-1.5 text-xs font-semibold text-blue-700 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 active:bg-blue-200 transition-colors"
                  >
                    Max
                  </button>
                </div>
              </div>
            </div>

            {/* Espèces / Virement — two cases on one line */}
            <div className="grid grid-cols-2 gap-3 mt-3">
              {['Espèces', 'Virement'].map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => setPaymentMethod(m)}
                  className={`flex items-center justify-center px-4 py-3 rounded-xl border text-center font-medium transition-colors ${
                    paymentMethod === m
                      ? 'border-blue-500 bg-blue-50 text-blue-700'
                      : 'border-gray-200 hover:border-gray-300 text-gray-700'
                  }`}
                >
                  {m}
                </button>
              ))}
            </div>

            {/* Livraison — frais de livraison, hors total facture ; comptés
                comme charge dans les statistiques */}
            <div className="mt-3">
              <label className="block text-sm font-medium text-gray-600 mb-1.5">
                Livraison <span className="text-xs font-normal text-gray-400">(comptée en charge)</span>
              </label>
              <div className="relative">
                <input
                  type="number"
                  name="livraison"
                  value={factureDetails.livraison || ''}
                  onChange={handleFactureDetailsChange}
                  className="w-full pl-4 pr-12 py-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-1 focus:ring-gray-900 focus:border-gray-900"
                  min="0"
                  step="0.001"
                  placeholder="0.000"
                />
                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 text-sm">DT</span>
              </div>
            </div>

            {/* Composants-2 §10 : pied à droite — Annuler ghost + Confirmer primaire vert (.btn-p, owner 2026-09-10) */}
            <div className="mt-6 flex justify-end gap-3">
              <button type="button" onClick={() => setShowPaymentModal(false)} className="btn-g px-4 py-2.5">Annuler</button>
              <button
                onClick={handleSaveFacture}
                className="px-6 py-2.5 btn-p transition-colors"
              >
                <IcSave className="inline mr-2 -mt-0.5" />
                Confirmer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Nom de page = chip actif `.tab-chip-on`, comme /products/add et
          /clients/add (owner 2026-09-09). */}
      <div className="mb-4 flex items-center gap-3 flex-wrap">
        <h1 className="tab-chip-on cursor-default">
          <IcReceiptText size={13} /> {isAvoir
            ? (isEditMode ? 'Modifier la facture avoir' : 'Ajouter une facture avoir')
            : (isEditMode ? 'Modifier la facture' : appText('facture_add_title', 'Ajouter une nouvelle facture'))}
        </h1>
        {/* Facture d'achat | Retour fournisseur — pilule .segp À DROITE de la
            rangée, au même emplacement (ml-auto) que Produits | Services de
            Nouvelle vente (owner 2026-09-28). Retour = facture AVOIR :
            produits qui sortent du stock, montant déduit du fournisseur. */}
        {!isEditMode && !commandePrefill && (
          <div className="ml-auto inline-flex items-center gap-2 shrink-0">
          <div className="segp" aria-label="Facture d’achat ou retour fournisseur">
            <button type="button" onClick={() => setAvoirMode(false)} aria-pressed={!isAvoir}
              className={!isAvoir ? 'segp-on' : 'segp-off'}>
              <IcReceiptText size={13} /> Facture
            </button>
            <button type="button" onClick={() => setAvoirMode(true)} aria-pressed={isAvoir}
              title="Facture avoir (retour fournisseur) : les produits sortent du stock, le montant est déduit du fournisseur"
              className={isAvoir ? 'segp-on' : 'segp-off'}>
              <IcUndo size={13} /> Avoir
            </button>
          </div>
          </div>
        )}
      </div>

      {/* Loading indicator for edit mode */}
      {loadingFacture && (
        <div className="text-center py-10">
          <CardSkeleton />
        </div>
      )}

      {!loadingFacture && (
      /* Même page que « Ajouter un produit » : scission `.kp-split` 80 px sous
         le chip, carte de saisie à gauche, panneau collant à droite qui porte
         les actions. */
      <div className="kp-split mt-20">

        {/* ================= Colonne gauche : Détails de la facture + Produits ================= */}
        <div className="kp-pane bg-white border border-gray-200 rounded-2xl overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-2 flex-shrink-0">
            <span className="h-7 w-7 rounded-md bg-gray-100 text-gray-500 flex items-center justify-center shrink-0"><IcReceiptText size={14} /></span>
            <h2 className="text-sm font-bold text-gray-900">Détails de la facture</h2>
          </div>

          <div className="kp-scroll p-4 space-y-4">
            {/* Ligne 1 — Code | Numéro | Date */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="min-w-0">
                <label className={labelClass} htmlFor="facture-code">Code facture</label>
                <input
                  type="text"
                  id="facture-code"
                  name="code"
                  value={factureDetails.code}
                  onChange={handleFactureDetailsChange}
                  className={inputClass}
                  placeholder="Optionnel"
                />
              </div>
              <div className="min-w-0">
                <label className={labelClass} htmlFor="facture-number">{isAvoir ? 'N° avoir' : 'Numéro facture'} <span className="req">*</span></label>
                <input
                  type="text"
                  id="facture-number"
                  name="number"
                  value={factureDetails.number}
                  onChange={handleFactureDetailsChange}
                  className={inputClass}
                  placeholder="Numéro de document"
                  required
                />
              </div>
              <div className="min-w-0">
                <label className={labelClass} htmlFor="facture-date">{isAvoir ? 'Date de l’avoir' : 'Date de la facture'} <span className="req">*</span></label>
                <input
                  type="date"
                  id="facture-date"
                  name="date"
                  value={factureDetails.date}
                  onChange={handleFactureDetailsChange}
                  className={inputClass}
                  required
                />
              </div>
            </div>

            {/* ===== Produits — section de la même carte, séparée par un filet ===== */}
            <div className="pt-4 border-t border-gray-100 flex items-center gap-2">
              <span className="h-7 w-7 rounded-md bg-gray-100 text-gray-500 flex items-center justify-center shrink-0"><IcPackageOpen size={14} /></span>
              <h2 className="text-sm font-bold text-gray-900">Produits</h2>
            </div>

            {/* Recherche produit — champ `.inp` avec l'icône scan à gauche ; la
                liste `.ac-list` collée sous le champ, « Ajouter » en pied. */}
            <div className="relative">
              <IcScanBarcode className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 z-10" size={13} />
              <input
                type="text"
                value={productSearch}
                onChange={(e) => { setProductSearch(e.target.value); setShowProductDropdown(true); }}
                onClick={() => setShowProductDropdown(true)}
                onBlur={() => setTimeout(() => setShowProductDropdown(false), 150)}
                className={`${inputClass} pl-9`}
                placeholder="Rechercher un produit par nom ou code barre..."
              />

              {showProductDropdown && (
                <div className="ac-list mt-1 rounded-lg max-h-60 overflow-auto">
                  {filteredProducts.length > 0 ? (
                    filteredProducts.map(product => (
                      <div
                        key={product.id}
                        onMouseDown={(e) => e.preventDefault()}
                        className="ac-row gap-3"
                        onClick={() => handleSelectProduct(product)}
                      >
                        <span className="h-9 w-9 rounded-lg bg-gray-50 border border-gray-100 flex items-center justify-center overflow-hidden shrink-0">
                          {product.image
                            ? <img src={product.image} alt="" className="h-full w-full object-contain" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                            : <IcPackageOpen className="text-gray-300" size={14} />}
                        </span>
                        <div className="flex-1 min-w-0">
                          <div className="ac-name truncate">{product.name}</div>
                          <div className="ac-sub text-sm flex justify-between">
                            <span>Code: {product.barcode || 'N/A'}</span>
                            <span>Prix: {product.price?.toFixed(3) || 0} DT</span>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="px-4 py-2.5 text-gray-500 text-sm">
                      {productSearch.trim() ? 'Aucun produit trouvé' : 'Tapez un nom ou un code barre pour rechercher…'}
                    </div>
                  )}
                  <div
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => {
                      setShowProductDropdown(false);
                      // The typed search pre-fills the new product's name.
                      openCreatePop(productSearch.trim());
                    }}
                    className="px-4 py-2.5 cursor-pointer btn-add-block"
                  >
                    <IcPlus className="mr-2" />
                    Ajouter un nouveau produit
                  </div>
                </div>
              )}
            </div>

            {/* Lignes de la facture — en-têtes `.th-ki` du kit, toutes neutres ;
                modifier = clic sur la ligne (ouvre « Modifier la ligne »). */}
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr className="text-left">
                    <th className="th-ki px-3 py-2.5 uppercase rounded-l-lg">Produit</th>
                    <th className="th-ki px-3 py-2.5 uppercase">
                      <span className="sm:hidden">Prix HT</span>
                      <span className="hidden sm:inline">Prix d'achat HT</span>
                    </th>
                    <th className="th-ki px-3 py-2.5 uppercase text-center">
                      <span className="sm:hidden">Qté</span>
                      <span className="hidden sm:inline">Quantité</span>
                    </th>
                    <th className="th-ki hidden sm:table-cell px-3 py-2.5 uppercase">Montant TVA</th>
                    <th className="th-ki px-3 py-2.5 uppercase rounded-r-lg text-center">Remise</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {selectedProducts.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="px-4 py-10 text-center text-sm text-gray-400">
                        <IcPackageOpen className="mx-auto text-3xl text-gray-300 mb-2" />
                        Aucun produit ajouté
                      </td>
                    </tr>
                  ) : (
                    selectedProducts.map((product) => {
                      const montantTVA = (product.cost * product.quantity) * ((product.tva_rate || 0) / 100);
                      const totalTTC = (product.cost * product.quantity) * (1 + (product.tva_rate || 0) / 100);
                      return (
                        <tr
                          key={product.unique_id}
                          onClick={() => setEditingProductId(product.unique_id)}
                          className="hover:bg-gray-50/60 transition-colors cursor-pointer"
                        >
                          <td className="px-3 py-3 whitespace-nowrap">
                            <div className="flex items-center gap-2.5">
                              {(() => {
                                const img = product.image || products.find((p) => p.id === product.id)?.image;
                                return (
                                  <span className="h-9 w-9 rounded-lg bg-gray-50 border border-gray-100 flex items-center justify-center overflow-hidden shrink-0">
                                    {img
                                      ? <img src={img} alt="" className="h-full w-full object-contain" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                                      : <IcPackageOpen className="text-gray-300" size={14} />}
                                  </span>
                                );
                              })()}
                              <div className="min-w-0">
                                <div className="text-sm font-medium text-gray-900 truncate max-w-[6rem] sm:max-w-none">{product.name}</div>
                                <div className="text-xs text-gray-400 truncate max-w-[6rem] sm:max-w-none">{product.barcode || 'Sans code'}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-700 tabular-nums">
                            {(product.original_cost || 0).toFixed(3)} <span className="hidden sm:inline text-xs text-gray-400">DT</span>
                          </td>
                          <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-700 text-center tabular-nums">{isAvoir ? `−${product.quantity}` : product.quantity}</td>
                          <td className="hidden sm:table-cell px-3 py-3 whitespace-nowrap text-sm tabular-nums">
                            <span className="font-medium text-gray-900">{(sgn * montantTVA).toFixed(3)}</span>
                            <span className="text-gray-300 mx-1.5">|</span>
                            <span className="text-gray-500" title="Total TTC">{(sgn * totalTTC).toFixed(3)}</span>
                          </td>
                          <td className="px-3 py-3 whitespace-nowrap text-center">
                            {product.is_free ? (
                              <span className="bdg bdg-free">GRATUIT</span>
                            ) : product.is_discount ? (
                              <span className="bdg bdg-promo">−{product.discount_percentage || 0}%</span>
                            ) : (
                              <span className="text-gray-300">—</span>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* ================= Colonne droite : Fournisseur + Résumé financier (collante) ================= */}
        <div className="kp-card kp-pane bg-white border border-gray-200 rounded-2xl overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-2 flex-shrink-0">
            <span className="h-7 w-7 rounded-md bg-gray-100 text-gray-500 flex items-center justify-center shrink-0"><IcUser size={14} /></span>
            <h2 className="text-sm font-bold text-gray-900">Fournisseur <span className="req">*</span></h2>
          </div>

          <div className="kp-scroll p-4 space-y-4">
            {selectedSupplier ? (
              /* Fournisseur choisi — bloc neutre du kit, ✕ pour en changer */
              <div className="relative rounded-xl border border-gray-200 bg-gray-50 p-3 pr-9">
                <button
                  type="button"
                  onClick={() => setSelectedSupplier(null)}
                  className="absolute top-2 right-2 h-6 w-6 rounded-md flex items-center justify-center text-gray-400 hover:text-gray-900 hover:bg-gray-100 transition-colors"
                  title="Changer de fournisseur"
                  aria-label="Changer de fournisseur"
                >
                  <IcClose size={13} />
                </button>
                <p className="text-sm font-semibold text-gray-900">{selectedSupplier.name}</p>
                {(selectedSupplier.address || selectedSupplier.phone || selectedSupplier.email) && (
                  <div className="mt-1 text-xs text-gray-500 space-y-0.5">
                    {selectedSupplier.address && <p>{selectedSupplier.address}</p>}
                    {selectedSupplier.phone && <p className="tabular-nums">{selectedSupplier.phone}</p>}
                    {selectedSupplier.email && <p>{selectedSupplier.email}</p>}
                  </div>
                )}
              </div>
            ) : (
              <div>
                <div className="relative">
                  <IcSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 z-10" size={13} />
                  <input
                    type="text"
                    value={supplierSearch}
                    onChange={(e) => {
                      setSupplierSearch(e.target.value);
                      setShowSupplierDropdown(true);
                    }}
                    onClick={() => setShowSupplierDropdown(true)}
                    className={`${inputClass} pl-9`}
                    placeholder="Rechercher un fournisseur..."
                  />

                  {showSupplierDropdown && (
                    <div className="ac-list mt-1 rounded-lg max-h-60 overflow-auto">
                      {filteredSuppliers.length > 0 ? (
                        filteredSuppliers.map(supplier => (
                          <div
                            key={supplier.id}
                            className="ac-row"
                            onClick={() => handleSelectSupplier(supplier)}
                          >
                            <span className="ac-name truncate">{supplier.name}</span>
                            {supplier.phone && <span className="ac-sub text-sm shrink-0">{supplier.phone}</span>}
                          </div>
                        ))
                      ) : (
                        <div className="px-4 py-2.5 text-gray-500 text-sm">Aucun fournisseur trouvé</div>
                      )}
                      <div
                        className="px-4 py-2.5 cursor-pointer btn-add-block"
                        onClick={() => {
                          setShowSupplierDropdown(false);
                          setShowAddSupplier(true);
                        }}
                      >
                        <IcPlus className="mr-2" />
                        Ajouter un nouveau fournisseur
                      </div>
                    </div>
                  )}
                </div>
                <p className={helpClass}>Recherchez un fournisseur pour voir ses coordonnées.</p>
              </div>
            )}

            {/* Nouveau fournisseur — formulaire en place, champs du kit */}
            {showAddSupplier && (
              <div className="rounded-xl border border-gray-200 bg-gray-50 p-3">
                <p className="text-sm font-semibold text-gray-900 mb-3">Ajouter un nouveau fournisseur</p>
                <form onSubmit={handleAddNewSupplier}>
                  <div className="space-y-3 mb-3">
                    <div>
                      <label className={labelClass} htmlFor="new-supplier-name">Nom <span className="req">*</span></label>
                      <input
                        type="text"
                        id="new-supplier-name"
                        name="name"
                        value={newSupplier.name}
                        onChange={handleNewSupplierChange}
                        className={inputClass}
                        required
                      />
                    </div>
                    <div>
                      <label className={labelClass} htmlFor="new-supplier-phone">Téléphone</label>
                      <input
                        type="text"
                        id="new-supplier-phone"
                        name="phone"
                        value={newSupplier.phone}
                        onChange={handleNewSupplierChange}
                        className={inputClass}
                      />
                    </div>
                    <div>
                      <label className={labelClass} htmlFor="new-supplier-email">Email</label>
                      <input
                        type="email"
                        id="new-supplier-email"
                        name="email"
                        value={newSupplier.email}
                        onChange={handleNewSupplierChange}
                        className={inputClass}
                      />
                    </div>
                    <div>
                      <label className={labelClass} htmlFor="new-supplier-address">Adresse</label>
                      <input
                        type="text"
                        id="new-supplier-address"
                        name="address"
                        value={newSupplier.address}
                        onChange={handleNewSupplierChange}
                        className={inputClass}
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <button
                      type="button"
                      onClick={() => setShowAddSupplier(false)}
                      className="btn-g col-span-1 py-2 rounded-xl text-sm"
                    >
                      Annuler
                    </button>
                    <button
                      type="submit"
                      className="btn-add-pop col-span-2 py-2 rounded-xl text-sm"
                      disabled={loading}
                    >
                      {loading ? 'Ajout...' : 'Ajouter'}
                    </button>
                  </div>
                </form>
              </div>
            )}

            {/* ===== Résumé financier — section sous filet ===== */}
            <div className="pt-4 border-t border-gray-100 flex items-center gap-2">
              <span className="h-7 w-7 rounded-md bg-gray-100 text-gray-500 flex items-center justify-center shrink-0"><IcPieChart size={14} /></span>
              <h2 className="text-sm font-bold text-gray-900">Résumé financier</h2>
            </div>

            <div className="space-y-2.5">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Total HT</span>
                <span className="font-medium text-gray-900 tabular-nums">{(sgn * subTotalHT).toFixed(3)} DT</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Total TVA</span>
                <span className="font-medium text-gray-900 tabular-nums">{(sgn * totalTVA).toFixed(3)} DT</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Total TTC</span>
                <span className="font-medium text-gray-900 tabular-nums">{(sgn * totalTTC).toFixed(3)} DT</span>
              </div>
              {isAvoir ? (
              <div className="flex justify-between items-center text-sm pb-3 border-b border-gray-100">
                <label className="text-gray-500" htmlFor="avoir-remise">Remise % <span className="text-xs text-gray-400">(toutes les lignes)</span></label>
                <div className="relative w-28">
                  <input
                    type="number"
                    id="avoir-remise"
                    value={avoirRemise || ''}
                    onChange={(e) => applyAvoirRemise(e.target.value)}
                    className="inp w-full pl-2 pr-7 py-1.5 text-sm text-right text-gray-900 tabular-nums"
                    min="0"
                    max="100"
                    step="0.5"
                    placeholder="0"
                  />
                  <span className={suffixClass}>%</span>
                </div>
              </div>
              ) : (
              <div className="flex justify-between items-center text-sm pb-3 border-b border-gray-100">
                <label className="text-gray-500" htmlFor="facture-timbre">Timbre</label>
                <div className="relative w-28">
                  <input
                    type="number"
                    id="facture-timbre"
                    name="timbre"
                    value={factureDetails.timbre}
                    onChange={handleFactureDetailsChange}
                    onBlur={(e) => { if (e.target.value === '') handleFactureDetailsChange({ target: { name: 'timbre', value: 0 } }); }}
                    className="inp w-full pl-2 pr-9 py-1.5 text-sm text-right text-gray-900 tabular-nums"
                    min="0"
                    step="0.001"
                    placeholder="1.000"
                  />
                  <span className={suffixClass}>DT</span>
                </div>
              </div>
              )}
              <div className="flex justify-between items-center pt-1">
                <span className="text-sm font-bold text-gray-900">{isAvoir ? 'Total avoir' : 'Total'}</span>
                <span className="text-lg font-bold text-gray-900 tabular-nums">{(sgn * grandTotal).toFixed(3)} DT</span>
              </div>
              {/* Effet de l'avoir sur ce qu'on doit à ce fournisseur */}
              {isAvoir && selectedSupplier && !isEditMode && (() => {
                const due = parseFloat(selectedSupplier.total_amount_due) || 0;
                const used = Math.min(due, grandTotal);
                return (
                  <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-2.5 text-xs text-red-700 space-y-1">
                    <div className="flex justify-between gap-2"><span>Vous devez à {selectedSupplier.name}</span><span className="tabular-nums">{due.toFixed(3)} DT</span></div>
                    <div className="flex justify-between gap-2 font-semibold"><span>Après cet avoir</span><span className="tabular-nums">{Math.max(0, due - used).toFixed(3)} DT</span></div>
                    {grandTotal - used > 0.0005 && (
                      <div>Reste {(grandTotal - used).toFixed(3)} DT d’avoir, déduit des prochaines factures.</div>
                    )}
                  </div>
                );
              })()}
            </div>

            {/* Payment tracking — shown only when enabled from the Apparence panel */}
            {!isAvoir && appFlag('facture_show_payment', false) && (
              <div className="pt-4 border-t border-gray-100 space-y-3">
                <div>
                  <label className={labelClass} htmlFor="facture-amount-paid">Montant payé</label>
                  <div className="relative">
                    <input
                      type="number"
                      id="facture-amount-paid"
                      name="amount_paid"
                      value={factureDetails.amount_paid || ''}
                      onChange={handleFactureDetailsChange}
                      className={`${inputClass} pr-10 tabular-nums`}
                      min="0"
                      max={grandTotal}
                      step="0.001"
                    />
                    <span className={suffixClass}>DT</span>
                  </div>
                </div>
                <div className="flex justify-between items-center text-sm">
                  <span className="text-gray-500">Reste à payer</span>
                  <span className={`font-semibold tabular-nums ${factureDetails.amount_due > 0 ? 'text-red-600' : 'text-gray-900'}`}>
                    {factureDetails.amount_due?.toFixed(3) || '0.000'} DT
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Pied — même recette que /products/add : ghost 1/3, primaire 2/3.
              Enregistrer ouvre d'abord l'étape « méthode de paiement ». */}
          <div className="p-3 border-t border-gray-100 flex-shrink-0">
            <div className="grid grid-cols-3 gap-2">
              <Link to="/facturation/list" className="btn-g col-span-1 py-2.5 rounded-xl text-sm transition-colors">
                Annuler
              </Link>
              <button
                type="button"
                onClick={() => (isAvoir ? handleSaveFacture() : setShowPaymentModal(true))}
                className={`${isAvoir ? 'btn-db' : 'btn-p'} col-span-2 py-2.5 rounded-xl text-sm transition-colors`}
                disabled={!selectedSupplier || selectedProducts.length === 0}
              >
                {isAvoir
                  ? (<><IcUndo size={14} /> {isEditMode ? 'Mettre à jour l’avoir' : 'Enregistrer l’avoir'}</>)
                  : (<><IcSave size={14} /> {isEditMode ? 'Mettre à jour la facture' : 'Enregistrer la facture'}</>)}
              </button>
            </div>
          </div>
        </div>
      </div>
      )}
    </div>
  );
};

export default AddFacturation;
